import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, ArrowUpRight, Sparkles, MessageCircle, Phone, Wrench, Scissors, Car, Scale,
  Zap, CalendarCheck, CreditCard, FileText, Bell, UserCheck, Repeat, ThumbsUp, Handshake,
  Send, DollarSign, Users, HelpCircle, Clock,
} from "lucide-react";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6"; // teal highlight per spec
const orange = "#F97316"; // orange circuitry accent
const glass = "rgba(20,28,44,0.55)";

const STORIES = [
  {
    id: "car",
    icon: Car,
    tag: "The car salesperson",
    body: [
      "Imagine waking up, making a cup of coffee, opening your laptop, and seeing that your AI twin has already been working.",
      "For the car salesperson, your digital sales partner may have already answered leads, followed up with past customers, and set four appointments before you even got dressed.",
      "You have not made one phone call yet.",
    ],
  },
  {
    id: "salon",
    icon: Scissors,
    tag: "The beauty professional",
    body: [
      "Barbie owns a salon offering hair, nails, and facials.",
      "She wakes up before 7:00 a.m. and sees that 12 appointments have already been scheduled.",
      "Her day is already looking like a $600 day.",
      "She did not chase one customer. She did not answer one message.",
      "Her AI assistant followed up with clients, sent offers, answered questions, and filled her calendar.",
    ],
  },
  {
    id: "auto",
    icon: Wrench,
    tag: "The automotive shop",
    body: [
      "A customer calls Larry's Automotive and says: “I need an estimate on my radiator. How much will it cost?”",
      "The AI phone agent responds: “You called the right place. Tell me exactly what the vehicle is doing.”",
      "After listening, it explains that the problem may not be the radiator and offers a no-cost inspection.",
      "It schedules the appointment, sends the address, confirms the customer's information, and notifies the shop.",
      "Chris never had to stop working on the vehicle in front of him.",
    ],
  },
  {
    id: "law",
    icon: Scale,
    tag: "The attorney",
    body: [
      "A potential legal client visits a law firm's website at 11:30 at night.",
      "They may be dealing with an accident, divorce, custody issue, criminal charge, business dispute, or urgent legal problem.",
      "The AI intake agent responds immediately.",
      "It can collect preliminary case information, send intake forms, schedule a consultation, explain approved consultation fees, send secure payment links, and notify the correct staff member.",
      "It does not replace the attorney or provide unauthorized legal advice. It makes sure the law firm does not lose a qualified lead because nobody responded quickly enough.",
    ],
  },
];

const CAPABILITIES = [
  { icon: MessageCircle,   label: "Answer website visitors" },
  { icon: Phone,           label: "Answer phone calls" },
  { icon: Sparkles,        label: "Recognize buying signals" },
  { icon: HelpCircle,      label: "Handle common objections" },
  { icon: DollarSign,      label: "Explain approved pricing" },
  { icon: FileText,        label: "Send forms" },
  { icon: FileText,        label: "Collect documents" },
  { icon: CalendarCheck,   label: "Schedule appointments" },
  { icon: Send,            label: "Send payment links" },
  { icon: CreditCard,      label: "Confirm payments" },
  { icon: Repeat,          label: "Follow up with old customers" },
  { icon: ThumbsUp,        label: "Send approved offers" },
  { icon: Bell,            label: "Notify the business owner" },
  { icon: Handshake,       label: "Hand the customer to a human" },
  { icon: UserCheck,       label: "Build a customer summary" },
  { icon: Zap,             label: "Keep the sale moving" },
];

// A tiny hook that returns whether the ref has been scrolled into view.
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function FounderLetter() {
  return (
    <section
      id="letter"
      className="relative z-10 py-24 md:py-36 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${C.ink} 0%, #0B1524 60%, ${C.ink} 100%)` }}
      data-testid="founder-letter-section"
    >
      <CircuitryBg />
      <div className="relative mx-auto max-w-[1200px] px-6 md:px-10">
        <Preamble />
        <StoriesRail />
        <MajorStatement />
        <CapabilitiesGrid />
        <OfferBlock />
        <BotDemoTeaser />
      </div>
    </section>
  );
}

// ─── Subtle orange circuitry background with tiny animated blips ───
function CircuitryBg() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `linear-gradient(${orange}55 1px, transparent 1px), linear-gradient(90deg, ${orange}55 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            `radial-gradient(700px 400px at 10% 10%, ${teal}18, transparent 60%),` +
            `radial-gradient(700px 400px at 90% 90%, ${orange}12, transparent 60%)`,
        }}
      />
      {/* Animated light blips */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="absolute w-1 h-1 rounded-full animate-ping"
            style={{
              background: i % 2 === 0 ? teal : orange,
              boxShadow: `0 0 12px ${i % 2 === 0 ? teal : orange}`,
              top: `${18 + i * 15}%`,
              left: `${(i * 23) % 90}%`,
              animationDelay: `${i * 0.9}s`,
              animationDuration: "3.6s",
            }}
          />
        ))}
      </div>
    </>
  );
}

// ─── Preamble ───
function Preamble() {
  return (
    <div className="max-w-[860px] mx-auto text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-8"
           style={{ background: `${teal}18`, color: teal, border: `1px solid ${teal}66` }}>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: teal, boxShadow: `0 0 8px ${teal}` }} />
        A note from the founder
      </div>

      {/* Real founder portrait */}
      <div className="relative mx-auto mb-10 w-40 h-40 md:w-48 md:h-48" data-testid="founder-portrait">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(from 140deg, ${teal}, ${orange}, ${teal})`,
            filter: "blur(12px)",
            opacity: 0.55,
          }}
        />
        <div
          className="relative rounded-full overflow-hidden"
          style={{
            width: "100%",
            height: "100%",
            border: `2px solid ${teal}88`,
            boxShadow: `0 30px 80px -20px ${teal}66, inset 0 0 0 3px rgba(0,0,0,0.4)`,
          }}
        >
          <img
            src="/founder-chris.png"
            alt="Kit — Chris Garcia, founder of PlugitAI"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 18%" }}
            loading="lazy"
          />
        </div>
        <div
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] whitespace-nowrap"
          style={{
            background: "#0B1524",
            color: teal,
            border: `1px solid ${teal}66`,
          }}
        >
          — Kit · Founder
        </div>
      </div>

      <h2
        className="font-black tracking-[-0.02em] leading-[1.02]"
        style={{ fontSize: "clamp(2.2rem, 5vw, 4.6rem)", color: "white" }}
        data-testid="letter-headline"
      >
        PLEASE READ THIS —{" "}
        <span style={{ color: teal, textShadow: `0 0 40px ${teal}66` }}>
          even if you do nothing else today.
        </span>
      </h2>

      <div className="mt-10 space-y-5 text-lg md:text-xl leading-relaxed text-white/85 max-w-[720px] mx-auto">
        <p>Back in 2010, getting a professional website was expensive.</p>
        <p>
          Even after you paid for it, you practically needed an IT person just to log in, change a
          picture, update a service, or fix a sentence.
        </p>
        <p>Over time, websites became easier. Technology improved. Businesses moved online.</p>
        <p className="text-white font-semibold">Now AI is changing everything again.</p>
        <p className="pt-2" style={{ color: teal }}>Some businesses have figured it out.</p>
        <p style={{ color: orange }}>Most have not caught on yet.</p>
      </div>
    </div>
  );
}

// ─── Story cards ───
function StoriesRail() {
  return (
    <div className="mt-24 md:mt-32 space-y-8 md:space-y-12">
      {STORIES.map((s, i) => (
        <StoryCard key={s.id} story={s} flipped={i % 2 === 1} />
      ))}
    </div>
  );
}

function StoryCard({ story, flipped }) {
  const [ref, inView] = useInView(0.15);
  const Icon = story.icon;
  return (
    <div
      ref={ref}
      className={`relative rounded-3xl p-6 md:p-10 border transition-all duration-[900ms] ease-out ${flipped ? "md:pl-40" : "md:pr-40"}`}
      style={{
        background: glass,
        backdropFilter: "blur(14px)",
        borderColor: `${teal}33`,
        boxShadow: inView ? `0 40px 120px -40px ${teal}44` : "none",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(30px)",
      }}
      data-testid={`letter-story-${story.id}`}
    >
      {/* Icon disk */}
      <div
        className={`absolute top-6 md:top-1/2 md:-translate-y-1/2 left-6 ${flipped ? "md:left-6" : "md:left-auto md:right-6"} w-16 h-16 md:w-24 md:h-24 rounded-2xl flex items-center justify-center`}
        style={{
          background: `linear-gradient(135deg, ${teal} 0%, ${orange} 140%)`,
          boxShadow: `0 20px 60px -18px ${teal}88, inset 0 0 0 1px rgba(255,255,255,0.15)`,
        }}
      >
        <Icon size={32} className="text-white" strokeWidth={1.8} />
      </div>

      <div className="pt-24 md:pt-0">
        <div className="text-[10px] uppercase tracking-[0.24em] font-black mb-3" style={{ color: orange }}>
          {story.tag}
        </div>
        <div className="space-y-3 text-base md:text-lg leading-relaxed text-white/90">
          {story.body.map((line, i) => (
            <p key={i} className={i === story.body.length - 1 ? "font-semibold text-white" : ""}>{line}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Major statement ───
function MajorStatement() {
  return (
    <div className="mt-28 md:mt-36 text-center max-w-[1000px] mx-auto">
      <h3
        className="font-black tracking-[-0.02em] leading-[0.98]"
        style={{
          fontSize: "clamp(2.4rem, 6.4vw, 6rem)",
          color: "white",
          textShadow: `0 0 60px ${teal}55`,
        }}
        data-testid="letter-major-statement"
      >
        YOU DO NOT HAVE TO KNOW{" "}
        <span style={{ color: teal }}>ANYTHING</span> ABOUT{" "}
        <span
          style={{
            backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${orange} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          CODING.
        </span>
      </h3>
      <div className="mt-10 space-y-4 text-lg md:text-xl leading-relaxed text-white/85 max-w-[720px] mx-auto">
        <p>You tell us how your business works.</p>
        <p>
          We build the website, sales process, forms, scheduling, follow-up, payment experience,
          and AI assistant around your operation.
        </p>
        <p className="text-white font-semibold">You keep the human touch.</p>
        <p style={{ color: teal }}>
          The technology gives you another version of yourself that keeps working when you cannot.
        </p>
      </div>
    </div>
  );
}

// ─── Capabilities grid ───
function CapabilitiesGrid() {
  return (
    <div className="mt-28 md:mt-36" data-testid="letter-capabilities">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: teal }}>
          The system can
        </div>
        <h3 className="font-black tracking-[-0.02em] text-white" style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)" }}>
          16 things your AI twin does every day
        </h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {CAPABILITIES.map((cap, i) => (
          <CapCard key={i} idx={i} cap={cap} />
        ))}
      </div>
    </div>
  );
}

function CapCard({ idx, cap }) {
  const Icon = cap.icon;
  return (
    <div
      className="group relative rounded-2xl p-4 md:p-5 border transition-all duration-300 hover:-translate-y-0.5"
      style={{
        background: glass,
        backdropFilter: "blur(12px)",
        borderColor: idx % 3 === 0 ? `${teal}33` : `${orange}22`,
      }}
      data-testid={`letter-cap-${idx}`}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{
          background: idx % 2 === 0 ? `${teal}22` : `${orange}22`,
          border: `1px solid ${idx % 2 === 0 ? teal : orange}55`,
        }}
      >
        <Icon size={16} style={{ color: idx % 2 === 0 ? teal : orange }} />
      </div>
      <div className="text-sm md:text-[15px] font-semibold text-white leading-tight">{cap.label}</div>
    </div>
  );
}

// ─── Offer block ───
function OfferBlock() {
  return (
    <div
      id="letter-offer"
      className="mt-28 md:mt-36 relative rounded-3xl p-8 md:p-14 border overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${teal}18 0%, ${orange}12 100%), ${C.inkSoft}`,
        borderColor: `${teal}55`,
        boxShadow: `0 80px 200px -60px ${teal}55`,
      }}
      data-testid="letter-offer"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `radial-gradient(600px 300px at 90% -10%, ${orange}44, transparent 60%),
                            radial-gradient(600px 300px at 10% 110%, ${teal}55, transparent 60%)`,
        }}
      />
      <div className="relative">
        <h3
          className="font-black tracking-[-0.02em] leading-[1.02] text-white"
          style={{ fontSize: "clamp(1.8rem, 4vw, 3.2rem)" }}
        >
          WHAT WOULD ONE EXTRA CUSTOMER BE WORTH TO YOUR BUSINESS?
        </h3>
        <div className="mt-8 space-y-4 text-lg md:text-xl leading-relaxed text-white/85 max-w-[820px]">
          <p>
            We can build a professional website and intelligent sales system for many small
            businesses for{" "}
            <span className="text-white font-black">
              less than <span style={{ color: teal }}>$1,800</span>
            </span>
            , depending on the approved project scope.
          </p>
          <p>
            Lock in your project with{" "}
            <span
              className="font-black"
              style={{
                color: teal,
                textShadow: `0 0 24px ${teal}88`,
              }}
            >
              $299.99
            </span>{" "}
            and let us begin designing the system around your business.
          </p>
          <p className="text-white/70 italic">
            This is not a{" "}
            <span className="font-bold" style={{ color: "#EC4899" }}>“$50 template website.”</span>{" "}
            This is a real business system with real people behind it.
          </p>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#pricing"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("[data-testid=plugitai-chat-open]")?.click();
            }}
            className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base transition-all hover:-translate-y-0.5"
            style={{
              background: `linear-gradient(135deg, ${teal} 0%, ${orange} 100%)`,
              color: "white",
              boxShadow: `0 24px 60px -20px ${teal}aa`,
            }}
            data-testid="letter-cta-primary"
          >
            Show me what my AI twin could do <ArrowRight size={16} />
          </a>
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
            style={{ borderColor: `${teal}77`, color: "white" }}
            data-testid="letter-cta-secondary"
          >
            Lock in my project for $299.99 <ArrowUpRight size={16} />
          </a>
          <a
            href="mailto:cgarciathanksyou@gmail.com?subject=Returning%20client%20rate"
            className="text-sm underline underline-offset-4 transition-colors hover:text-white"
            style={{ color: teal }}
            data-testid="letter-cta-tertiary"
          >
            Already worked with us? Email Kit directly for your returning-client rate.
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Bot demo teaser ───
function BotDemoTeaser() {
  return (
    <div className="mt-28 md:mt-36 text-center" data-testid="letter-bot-demo">
      <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: orange }}>
        Try it yourself
      </div>
      <h3 className="font-black tracking-[-0.02em] text-white" style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}>
        Don&apos;t just read about it. <span style={{ color: teal }}>Talk to it.</span>
      </h3>
      <p className="mt-6 max-w-[720px] mx-auto text-lg leading-relaxed text-white/80">
        Ask the bot how it would work inside your business. It listens, recognizes buying signals,
        explains the opportunity, collects the essential information, and offers to send the
        project to Kit for design.
      </p>

      <button
        type="button"
        onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
        className="mt-8 inline-flex items-center gap-3 px-8 h-16 rounded-full font-bold text-base transition-all hover:-translate-y-0.5"
        style={{
          background: `linear-gradient(135deg, ${teal} 0%, ${orange} 100%)`,
          color: "white",
          boxShadow: `0 30px 80px -20px ${teal}99`,
        }}
        data-testid="letter-open-chat"
      >
        <MessageCircle size={18} />
        Open the sales agent
        <ArrowRight size={16} />
      </button>

      <div className="mt-6 flex items-center justify-center gap-2 text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
        <Clock size={12} />
        <span>Bot never invents prices, guarantees, timelines, or services — only approved information.</span>
      </div>
    </div>
  );
}
